import { useEventBulkUpdateMeetingTimeSeries } from "@crowdcoursing/api/openapi";
import { useCallback, useState } from "react";

import { useToast } from "./useToast";

export interface SeriesChange {
  added: { name: string | null; description: string | null }[];
  removed: { id: string | null }[];
}

const STRINGS = {
  FAILED_UPDATE: "Failed to update event series. Please try again.",
  SUCCESS_UPDATE: "Successfully updated event series!",
} as const;

interface UseEventSeriesProps {
  eventId: string;
  refetchEvent: () => Promise<unknown>;
}

export const useEventSeries = ({ eventId, refetchEvent }: UseEventSeriesProps) => {
  const [isSeriesDialogOpen, setIsSeriesDialogOpen] = useState(false);
  const toast = useToast();

  const { mutateAsync: bulkUpdateSeries } = useEventBulkUpdateMeetingTimeSeries();

  const handleManageSeries = useCallback(() => {
    setIsSeriesDialogOpen(true);
  }, []);

  const handleSaveSeries = useCallback(
    async (changes: SeriesChange) => {
      const added = changes.added.map((s) => ({
        seriesDescription: s.description || "",
        seriesName: s.name || "",
      }));
      const removed = changes.removed.map((s) => s.id).filter((id): id is string => Boolean(id));

      if (added.length === 0 && removed.length === 0) {
        setIsSeriesDialogOpen(false);
        return;
      }

      try {
        await bulkUpdateSeries({
          body: { added, removed },
          pathParams: { eventId },
        });
        toast.success(STRINGS.SUCCESS_UPDATE);
        await refetchEvent();
        setIsSeriesDialogOpen(false);
      } catch (error) {
        console.error("Failed to update event series:", error);
        toast.error(STRINGS.FAILED_UPDATE);
      }
    },
    [bulkUpdateSeries, eventId, refetchEvent, toast],
  );

  return {
    handleManageSeries,
    handleSaveSeries,
    isSeriesDialogOpen,
    onCloseSeriesDialog: () => setIsSeriesDialogOpen(false),
  };
};
